import Pristine from 'pristinejs';
import jump from 'jump.js';

Alpine.data('orderForm', (options = { defaultInputs: {} }) => {
	let autocomplete;
	const pristineConfig = {
		classTo: 'gfield',
		errorClass: 'gfield_error',
		successClass: 'gfield_success',
		errorTextParent: 'gfield',
		errorTextTag: 'div',
		errorTextClass: 'gfield_validation_message',
	};
	let validator;
	let validAutocomplete = false;
	const isAppeal = options.isAppeal || false;

	const formTrackingDetails = {
		interactionStartTime: null,
		isDirty: false,
		fieldHistory: [],
		isNotSubmit: true,
	};

	let submitType = 'postal order';
	let isLargeScreen = false;

	return {
		error: '',
		currentStep: 0,
		firstName: '',
		addressManualInput: false,
		loading: false,
		inputs: {
			skipped: false,
		},

		init() {
			setTimeout(() => {
				this.$store.aa.observeFormInteraction(
					this.$refs.step0form,
					formTrackingDetails
				);
				this.$store.aa.trackFormAbandonment(formTrackingDetails, {
					form_name: 'order form',
					form_variant: `${options.theme}`,
				});
			}, 1000);

			this.inputs.subscribe = !this.$store.aa.userInEU && options.hasNewsletter;

			this.inputs = {
				...this.inputs,
				...options.defaultInputs,
			};

			// Listen to when the Google Places Autocomplete script is ready for us to use.
			const interval = setInterval(() => {
				if (typeof google?.maps?.places?.Autocomplete === 'function') {
					clearInterval(interval);

					this.initAddressPlacesAutocomplete();
				}
			}, 500);

			this.listenToBrowserResize();
		},
		listenToBrowserResize() {
			// Media query must match the `md` breakpoint in our stylesheets.
			const mql = window.matchMedia('screen and (min-width: 992px)');

			const handleChange = () => {
				isLargeScreen = mql.matches;
			};

			if (typeof mql.addEventListener === 'function') {
				mql.addEventListener('change', () => {
					handleChange();
				});
			} else if (typeof mql.addListener === 'function') {
				mql.addListener(() => {
					handleChange();
				});
			}

			handleChange();
		},
		initAddressPlacesAutocomplete() {
			// Don't submit the form when users press enter in the google address field
			this.$refs.addressAutocomplete.addEventListener('keydown', (event) => {
				if (event.keyCode === 13) {
					event.preventDefault();
				}
			});

			const autocompleteOptions = {
				fields: ['address_components', 'geometry'],
				types: ['address'],
			};

			if (options.ausNZOnly) {
				// If set to AU/NZ only, only allow searching within those countries.
				autocompleteOptions.componentRestrictions = {
					country: ['au', 'nz'],
				};
			} else if (options.isAustralia) {
				/*
				Else if this is the AA site, then just prioritise AU results.
				https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletionRequest.bounds
				Autocomplete results will be biased towards, but not restricted to, the given bounds.
				These coordinates form a rectangle that encompasses Australia.
				 */
				autocompleteOptions.bounds = {
					north: -10.058203756024747,
					east: 153.64120969419173,
					south: -43.62142072042754,
					west: 112.91095945812877,
				};
			}

			autocomplete = new google.maps.places.Autocomplete(
				this.$refs.addressAutocomplete,
				autocompleteOptions
			);

			const findComponent = (components, type, name = 'long_name') => {
				if (!components?.length) return '';

				const component = components.find((c) => c.types.includes(type));

				return (component && component[name]) || '';
			};

			// Make sure the places autocomplete field is registered with Alpine.
			google.maps.event.addListener(autocomplete, 'place_changed', () => {
				// Reset the address fields.
				this.inputs.address = '';
				this.inputs.city = '';
				this.inputs.state = '';
				this.inputs.postcode = '';
				this.inputs.country = '';

				const place = autocomplete.getPlace();
				validAutocomplete = !!place.address_components;

				let subPremise = findComponent(
					place.address_components,
					'subpremise',
					'long_name'
				);
				const streetNumber = findComponent(
					place.address_components,
					'street_number',
					'long_name'
				);
				const streetName = findComponent(
					place.address_components,
					'route',
					'long_name'
				);

				subPremise = subPremise ? `${subPremise}/` : '';

				this.inputs.address =
					`${subPremise}${streetNumber} ${streetName}`.trim();
				this.inputs.city = findComponent(
					place.address_components,
					'locality',
					'long_name'
				);
				this.inputs.postcode = findComponent(
					place.address_components,
					'postal_code',
					'long_name'
				);
				this.inputs.country = findComponent(
					place.address_components,
					'country',
					'short_name'
				);

				// State should be the short name for AU, long name for anything else.
				this.inputs.state = findComponent(
					place.address_components,
					'administrative_area_level_1',
					this.inputs.country === 'AU' ? 'short_name' : 'long_name'
				);

				this.$nextTick(() => {
					this.initStep1Validator();
					validator.validate();
				});
			});
		},
		initStep0Validator() {
			validator && validator.destroy();
			validator = new Pristine(this.$refs.step0form, pristineConfig);
		},
		initStep1Validator() {
			validator.destroy();
			validator = new Pristine(this.$refs.step1form, pristineConfig);

			validator.addValidator(
				this.$refs.addressAutocomplete,
				() => this.addressManualInput || validAutocomplete,
				'Please select a valid address'
			);
			validator.addValidator(
				this.$refs.addressAutocomplete,
				() => {
					if (!this.addressManualInput && !validAutocomplete) return true;

					// validate all non-autocomplete fields to see if they are valid
					// if any are invalid, then the autocomplete field should display an error
					const otherFilterFields = validator.fields.filter(
						(f) => f.input !== this.$refs.addressAutocomplete
					);
					const otherFields = otherFilterFields.map((f) => f.input);

					return validator.validate(otherFields, true);
				},
				'Invalid address. Please enter your address manually'
			);
		},
		handleStep0Submit() {
			this.initStep0Validator();
			const valid = validator.validate();

			if (valid) {
				this.firstName = this.inputs.firstName;

				this.currentStep = 1;

				// When not on an appeal page, OR when on appeal page and on mobile.
				if (!isAppeal || (isAppeal && !isLargeScreen)) {
					this.scrollToTopOfForm();
				}
			}
		},
		handleStep1Submit() {
			this.initStep1Validator();
			const valid = validator.validate();

			if (valid) {
				this.sendRequest();
			}
		},
		scrollToTopOfForm() {
			jump(this.$refs.container, {
				duration: 300,
				offset: -100,
				a11y: true,
			});
		},
		skipStep(e) {
			e.preventDefault();
			this.inputs.skipped = true;
			submitType = 'digital order';

			this.sendRequest();
		},
		sendRequest() {
			this.loading = true;

			this.$store.aa
				.api('order-form')
				.post(this.inputs)
				.json((response) => {
					this.loading = false;
					if (response.success === true) {
						this.trackEvent(response.data || {});

						if (isAppeal) {
							this.$dispatch('appealformfocus');
							this.$dispatch('activateconfetti', options.randomID);

							setTimeout(() => {
								this.currentStep = 2;
							}, 250);
						} else {
							this.currentStep = 2;
							this.scrollToTopOfForm();
						}
					} else {
						this.error = 'Something went wrong!';
					}
				})
				.catch((error) => {
					this.loading = false;
					this.error =
						error?.json?.message ||
						'Something went wrong. Please reach out to the Animals Australia team.';
				});
		},
		trackEvent(responseData = {}) {
			this.$store.aa.track({
				event: 'form_submit',
				form_name: 'order form',
				form_variant: `${options.theme}`,
				submit_type: `${submitType}`,
				data: {
					...this.inputs,
					...responseData,
				},
			});

			formTrackingDetails.isNotSubmit = false;
		},
	};
});
